/**
 * StandardDialog – A modal dialog with header, scrollable content, and primary/secondary actions.
 * @module StandardDialog
 */
import { css, cx } from "@emotion/css";
import { XmarkIcon } from "@octopusdeploy/design-system-icons";
import { space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type React from "react";
import type { ButtonBasicProps, ButtonLinkProps } from "../Button";
import { Button } from "../Button";
import { ErrorPanel } from "../Page";
import type { ErrorInfo } from "../Page";
import type { ProgressTrackerProps } from "../ProgressTracker";
import { DialogBase } from "./DialogBase";
/** Props allowed for primary/secondary dialog buttons (basic button or link variant). */
type LimitedButtonProps = Pick<ButtonBasicProps, "label" | "onClick" | "disabled" | "importance"> | Pick<ButtonLinkProps, "label" | "onClick" | "href" | "external" | "importance" | "disabled">;
/**
 * Props for the StandardDialog component.
 * @property {string} title - Dialog title shown in the header. Recommend to keep this short and descriptive.
 * @property {boolean} open - Whether the dialog is visible.
 * @property {boolean} [showDismissButton=true] - Whether to show the header close (X) button.
 * @property {() => void} onClose - Called when the dialog should close (backdrop, escape, or dismiss).
 * @property {LimitedButtonProps} primaryButton - Config for the primary action button.
 * @property {LimitedButtonProps} [secondaryButton] - Optional config for a secondary action button.
 * @property {React.ReactNode} children - Main dialog content (scrollable body).
 * @property {React.ReactNode} [progressTracker] - Optional progress tracker slot, shown above the error panel.
 * @property {ErrorInfo[]} [errors] - Optional errors to display above the content.
 * @property {"small" | "medium" | "large"} width - Dialog width preset (small/medium/large).
 */
export type StandardDialogProps = {
    title: string;
    open: boolean;
    showDismissButton?: boolean;
    onClose: () => void;
    primaryButton: LimitedButtonProps;
    secondaryButton?: LimitedButtonProps;
    children: React.ReactNode;
    progressTracker?: React.ReactElement<ProgressTrackerProps>;
    errors?: ErrorInfo[];
    width: "small" | "medium" | "large";
};
/**
 * A standard modal dialog with a title, optional dismiss button, scrollable content area,
 * optional error panel, and primary/secondary footer actions. Uses DialogBase for focus
 * trap and accessibility.
 *
 * @param {StandardDialogProps} props - StandardDialog props.
 * @returns The rendered dialog.
 */
export function StandardDialog({ width = "medium", children, open, onClose, title, primaryButton, secondaryButton, progressTracker, errors, showDismissButton = true }: StandardDialogProps) {
    const closeOnBackdropClick = showDismissButton ?? true;
    return (<DialogBase open={open} onClose={onClose} accessibleName={title} autoFocusFirstElement={false} closeOnBackdropClick={closeOnBackdropClick} closeOnEscape={true}>
            <div className={cx(styles.container, standardDialogSizeStyles[width])}>
                <header className={styles.header}>
                    <h2 className={styles.title}>{title}</h2>
                    {showDismissButton && (<div className={styles.dismissButton}>
                            <Button importance="ghost" accessibleName={"Close dialog"} onClick={onClose} disabled={false} size="medium" icon={<XmarkIcon size={20}/>}/>
                        </div>)}
                </header>
                <StandardDialogContent>
                    {progressTracker && <div className={styles.progressTracker}>{progressTracker}</div>}
                    {errors && (<div className={styles.errorPanel}>
                            {errors.map((err, index) => (<ErrorPanel error={err} key={index}/>))}
                        </div>)}
                    {children}
                </StandardDialogContent>
                <footer className={styles.footer}>
                    {secondaryButton && <Button {...secondaryButton}/>}
                    <Button {...primaryButton}/>
                </footer>
            </div>
        </DialogBase>);
}
/** Props for the internal scrollable content wrapper. */
interface StandardDialogContentProps {
    children: React.ReactNode;
}
/** Scrollable content area with typography and focus styles. */
function StandardDialogContent({ children }: StandardDialogContentProps) {
    return <div className={standardDialogContentStyles}>{children}</div>;
}
const standardDialogSizeStyles = {
    small: css({
        width: `min(480px, calc(100vw - ${space[24]} * 2))`,
        maxHeight: "480px",
    }),
    medium: css({
        width: `min(600px, calc(100vw - ${space[24]} * 2))`,
        maxHeight: "600px",
    }),
    large: css({
        width: `min(800px, calc(100vw - ${space[24]} * 2))`,
        maxHeight: "800px",
    }),
};
const standardDialogContentStyles = css({
    display: "flex",
    flexDirection: "column",
    padding: space[24],
    gap: space[12],
    alignItems: "stretch",
    alignSelf: "stretch",
    font: text.body.regular.large,
    color: themeTokens.color.text.primary,
    minHeight: 0,
    maskImage: "linear-gradient(to bottom, white 0%, white 95%, transparent 100%)",
    overflowY: "auto",
    // This will handle some basic typography styles for the content to prevent inconsistent styling.
    // TODO: should replace with a more robust typography system/layout system.
    "& p": {
        margin: 0,
        font: text.body.regular.large,
        textBox: "trim-both text",
    },
    "& h3": {
        font: text.heading.small,
        margin: 0,
        textBox: "trim-both text",
    },
    "& ul": {
        marginTop: 0,
        marginBottom: 0,
        font: text.body.regular.large,
        textBox: "trim-both text",
    },
    "& p + h3, & p + div": {
        marginTop: space[12],
    },
});
const styles = {
    container: css({
        position: "relative",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        containerType: "inline-size",
    }),
    header: css({
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        gap: space[24],
        padding: `${space[24]} ${space[24]} ${space[16]} ${space[24]}`,
        marginBottom: space[2],
        borderBottom: `1px solid ${themeTokens.color.border.primary}`,
        ":has(button)": {
            paddingTop: space[16],
        },
    }),
    title: css({
        font: text.heading.medium,
        margin: 0,
        textBox: "trim-both text",
        flexWrap: "wrap",
    }),
    dismissButton: css({
        alignSelf: "flex-start",
    }),
    footer: css({
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "flex-end",
        gap: space[12],
        padding: `${space[8]} ${space[24]} ${space[24]} ${space[24]}`,
        [`@container (max-width: 350px)`]: {
            flexDirection: "column",
            alignItems: "stretch",
            // TODO: should consider a more robust solution for this, like a layout system.
            "& > button, & > a": {
                width: "100%",
            },
        },
    }),
    progressTracker: css({
        marginBottom: space[12],
    }),
    errorPanel: css({
        marginBottom: space[12],
    }),
};
